import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface DeliveryRecord {
  id: string
  event_id: string
  notification_id: string | null
  recipient_member_id: string | null
  channel: 'slack' | 'notion' | 'email' | 'web_push'
  provider: string | null
  provider_ref: string | null
  provider_url: string | null
  status: 'pending' | 'sent' | 'failed' | 'blocked' | 'skipped'
  payload: Record<string, unknown>
  attempts: number
  last_error: string | null
  next_attempt_at: string | null
  sent_at: string | null
  created_at: string
  updated_at: string
}

interface DeliveriesResponse {
  deliveries?: DeliveryRecord[]
  warning?: string
  error?: string
}

interface DispatchResponse {
  dispatch?: { count?: number; ok?: boolean; results?: Array<{ ok?: boolean; error?: string }> }
  error?: string
}

function formatDate(value: string | null) {
  if (!value) return 'Not set'
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function textValue(value: unknown) {
  return typeof value === 'string' ? value : ''
}

export default function AdminIntegrationsPage() {
  const { isLoggedIn, loading, status, rights } = useAuth()
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([])
  const [filterStatus, setFilterStatus] = useState('pending')
  const [filterChannel, setFilterChannel] = useState('')
  const [loadingDeliveries, setLoadingDeliveries] = useState(true)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const canAdmin = isLoggedIn && status === 'approved' && rights['integrations.delivery.manage'] === true

  async function loadDeliveries() {
    setLoadingDeliveries(true)
    setError(null)
    const params = new URLSearchParams()
    if (filterStatus) params.set('status', filterStatus)
    if (filterChannel) params.set('channel', filterChannel)
    try {
      const res = await fetch(`/api/admin/integrations/deliveries?${params.toString()}`, { credentials: 'include' })
      const data = await res.json() as DeliveriesResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to load deliveries')
      setDeliveries(data.deliveries ?? [])
      if (data.warning) setMessage('Integration delivery migration is not fully applied yet.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load deliveries')
    } finally {
      setLoadingDeliveries(false)
    }
  }

  useEffect(() => {
    if (canAdmin) void loadDeliveries()
  }, [canAdmin, filterStatus, filterChannel])

  async function updateDelivery(id: string, action: 'retry' | 'skip' | 'block') {
    setUpdatingId(id)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/admin/integrations/deliveries', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, action }),
      })
      const data = await res.json() as { error?: string }
      if (!res.ok) throw new Error(data.error ?? 'Unable to update delivery')
      setMessage(`Delivery ${action} saved.`)
      await loadDeliveries()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update delivery')
    } finally {
      setUpdatingId(null)
    }
  }

  async function dispatchSlack(id?: string) {
    setUpdatingId(id ?? 'slack:all')
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/admin/integrations/deliveries', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'dispatch_slack', id, limit: 10 }),
      })
      const data = await res.json() as DispatchResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to dispatch Slack deliveries')
      const failures = data.dispatch?.results?.filter(result => result.ok === false).length ?? 0
      const count = data.dispatch?.count ?? 0
      setMessage(failures ? `Slack dispatch attempted ${count}; ${failures} need attention.` : `Slack dispatch sent ${count} pending item${count === 1 ? '' : 's'}.`)
      await loadDeliveries()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to dispatch Slack deliveries')
    } finally {
      setUpdatingId(null)
    }
  }

  if (loading) {
    return <div className="admin-page"><p className="admin-muted">Checking integration access...</p></div>
  }

  if (!isLoggedIn) return <Navigate to="/signin" replace />

  if (!canAdmin) {
    return (
      <div className="admin-page">
        <span className="legal-eyebrow">Admin</span>
        <h1 className="admin-heading">Admin access required.</h1>
        <p className="admin-muted">This page is only available to approved Yan-Vada admins.</p>
        <Link to="/" className="btn btn-secondary">Back to public home</Link>
      </div>
    )
  }

  const pendingCount = deliveries.filter(delivery => delivery.status === 'pending').length
  const failedCount = deliveries.filter(delivery => delivery.status === 'failed').length
  const blockedCount = deliveries.filter(delivery => delivery.status === 'blocked').length

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <span className="legal-eyebrow">Admin · integrations</span>
          <h1 className="admin-heading">Integration deliveries</h1>
          <p className="admin-muted">
            Queue management for Slack, Notion, and future delivery workers. Slack dispatch uses Cloudflare secrets only.
          </p>
        </div>
        <div className="admin-stats" aria-label="Delivery counts">
          <div><strong>{pendingCount}</strong><span>Pending</span></div>
          <div><strong>{failedCount}</strong><span>Failed</span></div>
          <div><strong>{blockedCount}</strong><span>Blocked</span></div>
        </div>
      </header>

      <section className="admin-card admin-integration-filters" aria-label="Delivery filters">
        <label>
          <span>Status</span>
          <select value={filterStatus} onChange={event => setFilterStatus(event.target.value)}>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="blocked">Blocked</option>
            <option value="sent">Sent</option>
            <option value="skipped">Skipped</option>
          </select>
        </label>
        <label>
          <span>Channel</span>
          <select value={filterChannel} onChange={event => setFilterChannel(event.target.value)}>
            <option value="">All channels</option>
            <option value="slack">Slack</option>
            <option value="notion">Notion</option>
            <option value="email">Email</option>
            <option value="web_push">Web push</option>
          </select>
        </label>
        <button className="btn btn-secondary" type="button" onClick={() => void loadDeliveries()}>Refresh</button>
        <button className="btn btn-primary" type="button" disabled={updatingId === 'slack:all'} onClick={() => void dispatchSlack()}>
          {updatingId === 'slack:all' ? 'Sending Slack...' : 'Send pending Slack'}
        </button>
      </section>

      {(message || error) && (
        <div className={`admin-alert${error ? ' admin-alert-error' : ''}`} role={error ? 'alert' : 'status'}>
          {error ?? message}
        </div>
      )}

      <section className="admin-list" aria-label="Integration delivery list">
        {loadingDeliveries ? (
          <div className="admin-card"><p className="admin-muted">Loading deliveries...</p></div>
        ) : deliveries.length === 0 ? (
          <div className="admin-card"><p className="admin-muted">No deliveries for this filter.</p></div>
        ) : deliveries.map(delivery => {
          const title = textValue(delivery.payload.title) || delivery.event_id
          const body = textValue(delivery.payload.body)
          return (
            <article key={delivery.id} className="admin-card admin-integration-delivery">
              <div className="admin-member-main">
                <div>
                  <span className={`notification-priority notification-priority-${delivery.status}`}>{delivery.channel}</span>
                  <h2>{title}</h2>
                  {body && <p className="admin-muted">{body}</p>}
                </div>
                <span className={`admin-status admin-status-${delivery.status}`}>{delivery.status}</span>
              </div>
              <dl className="admin-rights-grid">
                <div><dt>Event</dt><dd>{delivery.event_id}</dd></div>
                <div><dt>Attempts</dt><dd>{delivery.attempts}</dd></div>
                <div><dt>Next attempt</dt><dd>{formatDate(delivery.next_attempt_at)}</dd></div>
                <div><dt>Created</dt><dd>{formatDate(delivery.created_at)}</dd></div>
              </dl>
              {delivery.last_error && <p className="admin-muted">Last error: {delivery.last_error}</p>}
              {delivery.provider_url && <a href={delivery.provider_url} target="_blank" rel="noopener noreferrer">Open provider record</a>}
              <div className="admin-member-actions">
                <button className="btn btn-secondary" type="button" disabled={updatingId === delivery.id} onClick={() => void updateDelivery(delivery.id, 'retry')}>Retry</button>
                {delivery.channel === 'slack' && (
                  <button className="btn btn-primary" type="button" disabled={updatingId === delivery.id} onClick={() => void dispatchSlack(delivery.id)}>Send Slack</button>
                )}
                <button className="btn btn-secondary" type="button" disabled={updatingId === delivery.id} onClick={() => void updateDelivery(delivery.id, 'block')}>Block</button>
                <button className="btn btn-danger" type="button" disabled={updatingId === delivery.id} onClick={() => void updateDelivery(delivery.id, 'skip')}>Skip</button>
              </div>
            </article>
          )
        })}
      </section>
    </div>
  )
}
